import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Grid, Tab,
} from '@material-ui/core';
import {
  TabList, TabContext, Alert,
} from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import type { AxiosError } from 'axios';
import axios from 'axios';
import RuleFilter from '../shared/RuleFilters';
import RuleResults from './ViewAllRules/RuleResults';
import type {
  IViewAllRulesQuery,
  IRule,
  IRuleCondition,
  IRuleOutcome,
  POLICYCONFIGURATORSECTIONS,
} from '../shared/Utilities/PolicyConfiguratorInterfaces';
import {
  emptyRuleCondition,
} from '../shared/Utilities/PolicyConfiguratorInterfaces';
import type { ICompany } from '../../massReceive/MassReceiveInterfaces';
import {
  emptyCompany,
  getEnumValues, getWorkflowFromPolicyConfiguratorSection, initialOutcomesForWorkflow,
} from '../shared/Utilities/PolicyConfiguratorHelpers';

const ViewAllRules: React.FC<{ policyConfiguratorSection: POLICYCONFIGURATORSECTIONS }> = ({ policyConfiguratorSection }) => {
  // Imports Initialization
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [searchParams] = useSearchParams();

  const workFlowTypeParam = searchParams.get('workflow');
  const workflowOptionsEnum = getWorkflowFromPolicyConfiguratorSection(policyConfiguratorSection);
  const workflowOptions = getEnumValues(workflowOptionsEnum);
  const initialWorkflowName = (workFlowTypeParam === null ? workflowOptions[0] : workFlowTypeParam);

  const companyNameParam = searchParams.get('company');
  const initialCompany = (companyNameParam === null ? undefined : { companyName: companyNameParam, companyId: '' });

  const [workflowName, setWorkflowName] = React.useState<string>(initialWorkflowName);

  const [company, setCompany] = React.useState<ICompany | undefined>(initialCompany);
  const [conditions, setConditions] = React.useState<IRuleCondition[]>([emptyRuleCondition()]);
  const [outcomes, setOutcomes] = React.useState<IRuleOutcome[]>([...initialOutcomesForWorkflow(workflowName)]);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [resultSet, setResultSet] = React.useState<IRule[]>(new Array<IRule>());
  const [ruleResultsPage, setRuleResultsPage] = React.useState<number>(1); // This will keep track of the page we are at in RuleResults

  // Methods
  const filterSet: IViewAllRulesQuery = {
    company,
    conditions,
    outcomes,
    workflowName,
  };

  const handleFetchRules = () => {
    setLoading(true);
    axios.post<IRule[]>('/rules/getRules', filterSet)
      .then((response) => setResultSet(response.data))
      .catch((err: AxiosError) => {
        if (err.response?.data === 'No rules found.') {
          enqueueSnackbar('No rules found.', { variant: 'error' });
          setResultSet([]);
        } else {
          enqueueSnackbar('Error fetching rules.', { persist: false, variant: 'error' });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteRule = (ruleNameParam: string | undefined) => {
    if (ruleNameParam) {
      axios.post('/rules/deleteRule', { RuleName: ruleNameParam })
        .then(() => {
          enqueueSnackbar('Rule deleted.');
          handleFetchRules();
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting the rule:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no rule selected to delete');
    }
  };

  const handleWorkflowTabChange = (event: unknown, newTabValue: string) => {
    setWorkflowName(newTabValue);
    // When changing tabs, company is still set even if the dropdown
    // hides... so we need to clear out the value before fetching non-company
    // related rules.
    setCompany(emptyCompany());
    setResultSet([]);
    setRuleResultsPage(1);
  };

  const handleClearAll = () => {
    setCompany(emptyCompany());
    setConditions([emptyRuleCondition()]);
    setOutcomes([...initialOutcomesForWorkflow(workflowName)]);
  };

  React.useEffect(() => {
    if (workFlowTypeParam && companyNameParam) {
      handleFetchRules();
    }
  // only want this to run once, on initial
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setOutcomes([...initialOutcomesForWorkflow(workflowName)]);
  }, [workflowName]);

  React.useEffect(() => {
    handleFetchRules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowName]);

  // Render
  return (
    <Grid container>
      <Grid item lg={1} />
      <Grid item lg={9} md={12} xs={12}>
        <TabContext value={workflowName}>
          <Box py={1}>
            <Alert severity="info">{'Start your Rules search by selecting a Workflow'}</Alert>
          </Box>
          <Box>
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              onChange={handleWorkflowTabChange}
            >
              {workflowOptions.map((w) => (
                <Tab label={w.replace('Destination - ', '')} value={w} />
              ))}
            </TabList>
          </Box>
          <RuleFilter
            workflowName={workflowName}
            workflowType={policyConfiguratorSection}
            filterMode="Filter"
            conditionFilterValues={conditions}
            setConditionFilterValues={setConditions}
            outcomeFilterValues={outcomes}
            setOutcomeFilterValues={setOutcomes}
            companyFilterValue={company}
            setCompanyFilterValue={setCompany}
            submitFilterValues={handleFetchRules}
            showErrors={false}
            handleClearAll={handleClearAll}
          />
          <RuleResults
            workflowName={workflowName}
            data={resultSet}
            loading={loading}
            handleDeleteRule={handleDeleteRule}
            currentPage={ruleResultsPage}
            setCurrentPage={setRuleResultsPage}
          />
        </TabContext>
      </Grid>
    </Grid>
  );
};

export default ViewAllRules;
